import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  GraphqlOperations,
  MessageBlastInput,
  UserSelectionInput,
  UserType,
} from "@src/types.generated";
import Text from "@src/lib/ui/Text";
import { emptyTranslationSet } from "@src/features/knowledgeCheck/utils/prompts";
import { useToast } from "@src/lib/hooks/useToast";
import useUserSelectionSetInputState from "@src/features/contentLibrary/assignmentDialogs/hooks/useUserSelectionSetInputState";
import UserSelectionSetPreview from "@src/features/contentLibrary/assignmentDialogs/UserSelectionSetPreview";
import { Label } from "@src/lib/ui/Label";
import useUser from "@src/features/app/hooks/useUser";
import {
  convertLocalDateTimeWithoutTzToUTCStringWithoutTz,
  isValidSendUntilDate,
} from "@src/lib/utils/dates";
import { useTracking } from "@src/features/app/hooks/useTracking";
import { pluralize } from "@src/lib/utils/strings";
import { launchConfetti } from "@src/lib/utils/confetti";
import NowOrScheduledPicker from "@src/lib/ui/NowOrScheduledPicker";
import ClockOutlineIcon from "@src/lib/icons/18px/clock-outline";
import NotificationSchedulePicker from "@src/lib/ui/NotificationSchedulePicker";
import { formatISO9075 } from "date-fns";
import SelectUsersDialog from "@src/features/contentLibrary/assignmentDialogs/SelectUsersDialog";
import { RadioGroup, RadioGroupItem } from "@src/lib/ui/RadioGroup";
import MessageBlastAttachments from "@src/features/messaging/MessageBlastAttachments";
import { useAvailableLanguages } from "@src/features/contentDetail/hooks/useAvailableLanguages";
import MessageBlastSubjectAndMessage from "@src/features/messaging/MessageBlastSubjectAndMessage";
import TranslationModeContextProvider from "@src/features/libraryItem/course/contexts/TranslationModeContextProvider";
import { useTranslation } from "@src/features/contentDetail/utils/translationSets";
import { isTranslationSetEmpty } from "@src/features/users/dialogs/utils";
import { isUserSelectionInputEmpty } from "@src/features/contentLibrary/utils/assignments";
import { gql } from "@apollo/client";
import {
  useCreateMessageBlastMutation,
  useUpdateMessageBlastMutation,
} from "./MessageBlastDialog.generated";
import {
  MessageBlastFragment,
  SentMessage_MessageBlastMediaFragment,
} from "./SentMessage.generated";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";

const REFETCH_QUERIES = [
  GraphqlOperations.Query.MessageBlasts,
  GraphqlOperations.Query.getMessageBlast,
];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setSelectedBlastId?: (id: number) => void;
  currentBlast?: MessageBlastFragment | null;
  userSelection?: UserSelectionInput;
  onSubmit?: () => void;
  duplicate?: boolean;
};

const MessageBlastDialog: FC<Props> = ({
  open,
  onOpenChange,
  setSelectedBlastId,
  currentBlast,
  userSelection,
  onSubmit,
  duplicate = false,
}) => {
  const { addToast, addErrorToast } = useToast();
  const { user } = useUser();
  const [selectUsersOpen, setSelectUsersOpen] = useState(false);
  const [selectUsersEditIndex, setSelectUsersEditIndex] = useState<
    number | null
  >(null);
  const trackEventState = useTracking();
  const { trackEvent } = trackEventState;
  const messageBlastId = currentBlast?.id;
  const initialScheduledTime = currentBlast?.sendAt;
  const initialFromOrg = currentBlast?.fromOrg;
  const initialSubject = currentBlast?.subject;
  const initialMessageText = currentBlast?.messageText;
  const initialSendReminderUntilTime =
    currentBlast?.notificationSchedule?.sendUntil;
  const initialMedias = currentBlast?.messageBlastMedias;

  const [messageBlastMedias, setMessageBlastMedias] = useState<
    Set<SentMessage_MessageBlastMediaFragment>
  >(initialMedias ? new Set(initialMedias) : new Set());

  const [sendFrom, setSendFrom] = useState<string>(
    initialFromOrg ? "org" : "user",
  );
  const onSendFromChange = (value: string) => {
    setSendFrom(value);
  };
  const [subjectText, setSubjectText] = useState(
    initialSubject || emptyTranslationSet(),
  );
  const [messageText, setMessageText] = useState(
    initialMessageText || emptyTranslationSet(),
  );

  const [scheduledTime, setScheduledTime] = useState<Date | null | undefined>(
    initialScheduledTime ? new Date(initialScheduledTime) : undefined,
  );

  const [sendReminderUntilTime, setSendReminderUntilTime] = useState<
    Date | undefined | null
  >(
    initialSendReminderUntilTime
      ? new Date(initialSendReminderUntilTime)
      : null,
  );
  const [isTranslationModeEnabled, setIsTranslationModeEnabled] =
    useState(false);
  const { mostPopularNonEnglishLanguage } = useAvailableLanguages();
  const [nonEnglishLanguage, setNonEnglishLanguage] = useState<Language>(
    mostPopularNonEnglishLanguage,
  );
  const { text: subjectTextTranslation } = useTranslation(
    subjectText,
    nonEnglishLanguage,
  );
  const { text: messageTextTranslation } = useTranslation(
    messageText,
    nonEnglishLanguage,
  );
  const sendAt = useMemo(() => {
    if (!scheduledTime) return undefined;
    return convertLocalDateTimeWithoutTzToUTCStringWithoutTz(scheduledTime);
  }, [scheduledTime]);
  const sendUntil = useMemo(() => {
    if (!sendReminderUntilTime) return undefined;
    return formatISO9075(new Date(sendReminderUntilTime));
  }, [sendReminderUntilTime]);

  const {
    userSelectionSetInput,
    deleteUserSelection,
    editUserSelection,
    addUserSelection,
  } = useUserSelectionSetInputState({
    initialUserSelectionSetFragment: currentBlast?.userSelectionSet,
    initialUserSelectionInput: userSelection,
  });

  const handleEditUserSelectionSet = useCallback((index: number) => {
    setSelectUsersEditIndex(index);
    setSelectUsersOpen(true);
  }, []);

  const handleRemoveUserSelectionSet = useCallback(
    (index: number) => {
      deleteUserSelection(index);
    },
    [deleteUserSelection],
  );

  const handleAddUserSelectionSet = useCallback(() => {
    setSelectUsersEditIndex(null);
    setSelectUsersOpen(true);
  }, []);

  const messageBlastMediasList = useMemo(() => {
    return Array.from(messageBlastMedias).map((mbm, i) => ({
      mediaId: mbm.media?.id,
      libraryItemId: mbm.libraryItem?.id,
      pinned: mbm.pinned,
      index: i,
    }));
  }, [messageBlastMedias]);

  const blastInput: MessageBlastInput = useMemo(() => {
    return {
      subject: subjectText,
      messageText: messageText,
      messageBlastMedias: messageBlastMediasList,
      userSelectionSet: userSelectionSetInput,
      fromOrg: sendFrom === "org" ? true : false,
      sendAt: sendAt,
      sendUntil: sendUntil,
    };
  }, [
    subjectText,
    messageText,
    userSelectionSetInput,
    sendFrom,
    sendAt,
    sendUntil,
    messageBlastMediasList,
  ]);

  const pinnedMedia = useMemo(() => {
    return Array.from(messageBlastMedias).find((x) => x.pinned) || null;
  }, [messageBlastMedias]);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const [createMessageBlastMutation, { loading: createMessageBlastLoading }] =
    useCreateMessageBlastMutation({
      variables: { input: blastInput },
      refetchQueries: REFETCH_QUERIES,
      onError: (error) => {
        addErrorToast({
          ...error,
          callsite: "create_blast",
          input: blastInput,
        });
      },
      onCompleted: (data) => {
        if (!data.createMessageBlast.success) {
          addErrorToast({
            data,
            callsite: "create_blast",
            input: blastInput,
          });
        } else {
          if (data.createMessageBlast.messageBlast?.id) {
            setSelectedBlastId?.(data.createMessageBlast.messageBlast?.id);
          }
          onSubmit && onSubmit();
          trackEvent("message_blast_success", {
            ...blastInput,
          });
          const recipientCount =
            data?.createMessageBlast?.messageBlast?.recipientCount;
          const messageIsDraft = data?.createMessageBlast?.messageBlast?.draft;
          if (!messageIsDraft) {
            launchConfetti();
          }
          if (scheduledTime) {
            addToast({
              message: "Message scheduled",
            });
          } else if (messageIsDraft) {
            addToast({
              message: `Message saved as draft`,
            });
          } else if (recipientCount) {
            addToast({
              message: `Message sent to ${recipientCount} ${pluralize(
                "trainee",
                recipientCount,
              )}!`,
            });
          } else {
            addToast({
              message: `Message sent!`,
            });
          }
          handleClose();
        }
      },
    });
  const [updateMessageBlastMutation, { loading: updateMessageBlastLoading }] =
    useUpdateMessageBlastMutation({
      refetchQueries: REFETCH_QUERIES,
      onError: (error) => {
        addErrorToast({
          ...error,
          callsite: "update_blast",
          input: blastInput,
        });
      },
      onCompleted: (data) => {
        if (!data.updateMessageBlast.success) {
          addErrorToast({
            data,
            callsite: "update_blast",
            input: blastInput,
          });
        } else {
          if (data.updateMessageBlast.messageBlast?.id) {
            setSelectedBlastId?.(data.updateMessageBlast.messageBlast?.id);
          }
          onSubmit && onSubmit();
          trackEvent("update_message_blast_success", {
            num: data?.updateMessageBlast?.messageBlast?.recipientCount || 0,
            ...blastInput,
          });
          const recipientCount =
            data?.updateMessageBlast?.messageBlast?.recipientCount;
          const messageIsDraft = data?.updateMessageBlast?.messageBlast?.draft;
          if (!messageIsDraft) {
            launchConfetti();
          }
          if (scheduledTime) {
            addToast({ message: "Message updated" });
          } else if (messageIsDraft) {
            addToast({
              message: `Message saved as draft`,
            });
          } else if (recipientCount) {
            addToast({
              message: `Message sent to ${recipientCount} ${pluralize(
                "trainee",
                recipientCount,
              )}!`,
            });
          } else {
            addToast({
              message: `Message sent!`,
            });
          }
          handleClose();
        }
      },
    });

  const sendButtonDisabled =
    isTranslationSetEmpty(messageText) ||
    !userSelectionSetInput.userSelections.some(
      (selection) => !isUserSelectionInputEmpty(selection),
    ) ||
    (scheduledTime ? scheduledTime < new Date() : false);

  useEffect(() => {
    setNonEnglishLanguage(mostPopularNonEnglishLanguage);
  }, [mostPopularNonEnglishLanguage]);

  const submitButtonText = useMemo(() => {
    if (currentBlast?.sentAt && !duplicate) {
      return "Update message";
    } else if (currentBlast?.sendAt && !duplicate) {
      if (scheduledTime) {
        return "Schedule message";
      } else {
        return "Send now";
      }
    } else if (scheduledTime) {
      return "Schedule message";
    }
    return "Send";
  }, [currentBlast, scheduledTime, duplicate]);
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90vh] max-w-[850px] flex-col gap-0 p-0">
          <TranslationModeContextProvider>
            <DialogHeader className="border-b border-input px-5 py-3">
              <DialogTitle>
                {currentBlast && !duplicate ? "Edit Message" : "New Message"}
              </DialogTitle>
            </DialogHeader>
            <div className="flex flex-1 flex-col self-stretch overflow-auto p-5">
              <div className="flex flex-col gap-2 self-stretch">
                {user?.userType === UserType.Admin && (
                  <div className="flex gap-4 pb-2">
                    <Text variant="compact-title">From</Text>
                    <RadioGroup
                      defaultValue={sendFrom}
                      disabled={!!currentBlast?.sentAt}
                      onValueChange={onSendFromChange}
                      className="flex gap-4 align-middle"
                    >
                      <div className="flex items-center gap-1">
                        <RadioGroupItem value="user" id="user" />
                        <Label htmlFor="user" className="cursor-pointer">
                          {user?.name || "You"}
                        </Label>
                      </div>
                      <div className="flex items-center gap-1">
                        <RadioGroupItem value="org" id="org" />
                        <Label htmlFor="org" className="cursor-pointer">
                          {user?.org?.name || "Your Organization"}
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
                <div className="flex flex-col">
                  <Text variant="compact-title" className="mt-2 pb-1">
                    Recipients
                  </Text>
                  <div className="flex flex-1 flex-col self-stretch pb-2">
                    <UserSelectionSetPreview
                      showCounts={true}
                      input={userSelectionSetInput}
                      showUserTypeOptions={true}
                      onEdit={handleEditUserSelectionSet}
                      onRemove={handleRemoveUserSelectionSet}
                      onAdd={handleAddUserSelectionSet}
                      noun="recipients"
                    />
                  </div>
                </div>
                <MessageBlastSubjectAndMessage
                  subjectText={subjectText}
                  subjectTranslation={subjectTextTranslation}
                  messageText={messageText}
                  messageTranslation={messageTextTranslation}
                  pinnedMedia={pinnedMedia}
                  setSubjectText={setSubjectText}
                  setMessageText={setMessageText}
                  nonEnglishLanguage={nonEnglishLanguage}
                  setNonEnglishLanguage={setNonEnglishLanguage}
                  messageBlastMedias={messageBlastMedias}
                  setMessageBlastMedias={setMessageBlastMedias}
                  isTranslationModeEnabled={isTranslationModeEnabled}
                  setIsTranslationModeEnabled={setIsTranslationModeEnabled}
                />
                <MessageBlastAttachments
                  messageBlastMedias={messageBlastMedias}
                  setMessageBlastMedias={setMessageBlastMedias}
                />
              </div>
            </div>
            <DialogFooter className="gap-8 border-t border-input px-5 py-3">
              <div className="flex">
                <NowOrScheduledPicker
                  messageSchedulingEnabled
                  leadingIcon={<ClockOutlineIcon />}
                  scheduledDateTime={scheduledTime}
                  onScheduledDateTimeChange={setScheduledTime}
                />
                <NotificationSchedulePicker
                  notifyUntilDate={sendReminderUntilTime}
                  scheduledDateTime={
                    scheduledTime ? new Date(scheduledTime) : null
                  }
                  onUpdateNotifyUntilDate={setSendReminderUntilTime}
                />
              </div>
              <div className="flex flex-1 justify-end gap-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                {!currentBlast?.sentAt && (
                  <Button
                    variant="outline"
                    loading={
                      createMessageBlastLoading || updateMessageBlastLoading
                    }
                    onClick={() => {
                      if (
                        blastInput.sendUntil &&
                        !isValidSendUntilDate(
                          new Date(blastInput.sendUntil!),
                          scheduledTime,
                        )
                      ) {
                        addErrorToast({
                          message:
                            "Notification reminder date must be later than scheduled date",
                          callsite: "message_blast_list_preview",
                        });
                      } else {
                        if (messageBlastId && !duplicate) {
                          updateMessageBlastMutation({
                            variables: {
                              id: messageBlastId,
                              input: {
                                ...blastInput,
                                draft: true,
                              },
                            },
                          });
                        } else {
                          createMessageBlastMutation({
                            variables: {
                              input: {
                                ...blastInput,
                                draft: true,
                              },
                            },
                          });
                        }
                      }
                    }}
                  >
                    Save Draft
                  </Button>
                )}
                <Button
                  disabled={sendButtonDisabled}
                  loading={
                    createMessageBlastLoading || updateMessageBlastLoading
                  }
                  onClick={() => {
                    if (
                      blastInput.sendUntil &&
                      !isValidSendUntilDate(
                        new Date(blastInput.sendUntil!),
                        scheduledTime,
                      )
                    ) {
                      addErrorToast({
                        message:
                          "Notification reminder date must be later than scheduled date",
                        callsite: "message_blast_list_preview",
                      });
                    } else {
                      if (messageBlastId && !duplicate) {
                        updateMessageBlastMutation({
                          variables: { id: messageBlastId, input: blastInput },
                        });
                      } else {
                        createMessageBlastMutation();
                      }
                    }
                  }}
                >
                  {submitButtonText}
                </Button>
              </div>
            </DialogFooter>
          </TranslationModeContextProvider>
        </DialogContent>
      </Dialog>
      {selectUsersOpen && (
        <SelectUsersDialog
          open={selectUsersOpen}
          onOpenChange={setSelectUsersOpen}
          input={
            selectUsersEditIndex !== null
              ? userSelectionSetInput.userSelections[selectUsersEditIndex]
              : {
                  allLocations: false,
                  allManagers: false,
                  allTrainees: false,
                  allAdmins: false,
                }
          }
          onSelect={(selection) => {
            if (selectUsersEditIndex !== null) {
              editUserSelection(selectUsersEditIndex, selection);
            } else {
              addUserSelection(selection);
            }
            setSelectUsersOpen(false);
          }}
          pillColor="purple"
          showUserTypeOptions
        />
      )}
    </>
  );
};

export default MessageBlastDialog;

gql`
  mutation CreateMessageBlast($input: MessageBlastInput!) {
    createMessageBlast(input: $input) {
      success
      error {
        code
      }
      messageBlast {
        id
        recipientCount
        draft
        subject {
          ...TranslationSet
        }
        messageText {
          ...TranslationSet
        }
      }
    }
  }

  mutation UpdateMessageBlast($id: Int!, $input: MessageBlastInput!) {
    updateMessageBlast(id: $id, input: $input) {
      success
      error {
        code
      }
      messageBlast {
        id
        recipientCount
        draft
        subject {
          ...TranslationSet
        }
        messageText {
          ...TranslationSet
        }
      }
    }
  }
`;
